import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import CircularProgress from '@mui/material/CircularProgress';
import { useUser } from '../../Hooks/useUser';
import axios from '../../api/axios';
import { useFormData } from "../../Hooks/useFormDataContext";

const endpoint = '/api/users/alluserinfo';

const Health = (props) => {
  const { user: contextUser } = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const { updateForm } = useFormData();

  // Admin mode passes User/UserSettings explicitly (even while still loading, as null).
  // Profile mode never passes these props at all.
  const isControlled = Object.prototype.hasOwnProperty.call(props, 'User');

  const [User, setUser] = useState(props.User ?? {});
  const [UserSettings, setUserSettings] = useState(props.UserSettings ?? {});
  const [saving, setSaving] = useState(false);

  // Admin mode: mirror whatever the parent fetched — no competing request here.
  useEffect(() => {
    if (!isControlled || !props.User) return;
    setUser(props.User);
  }, [isControlled, props.User]);

  useEffect(() => {
    if (!isControlled || !props.UserSettings) return;
    setUserSettings(props.UserSettings);
  }, [isControlled, props.UserSettings]);

  // Profile mode only: self-fetch based on the logged-in user.
  useEffect(() => {
    if (isControlled) return;
    if (!contextUser?.UserID) return;

    let cancelled = false;

    const fetchData = async () => {
      try {
        const result = await axios.get(`${endpoint}/${contextUser.UserID}`);
        if (cancelled) return;
        if (result?.data) {
          setUser(result.data.user || {});
          setUserSettings(result.data.userSettings || {});
        }
      } catch (error) {
        if (!cancelled) {
          console.error('ERROR fetching health data:', error);
          enqueueSnackbar('Health data fetch failed: ' + error.message, { variant: 'error' });
        }
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [isControlled, contextUser?.UserID, enqueueSnackbar]);

  function handleFieldChange(input) {
    const target = input.target;
    const value = target.value;
    const name = target.id;
    setUserSettings(prev => ({ ...prev, [name]: value }));
    if (props.UseFormContext) {
      updateForm("UserSettings", name, value);
    }
  }

  function handleCheckboxChange(input) {
    const target = input.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.id;
    setUserSettings(prev => ({ ...prev, [name]: value }));
    if (props.UseFormContext) {
      updateForm("UserSettings", name, value ? true : false);
    }
  }

  const isChecked = (value) => value === true || value === 1 || value === '1';

  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 80;
    const years = [];
    for (let year = currentYear; year >= startYear; year--) years.push(year);
    return years;
  };
  const yearsList = generateYears();

  const sanitizeUserSettings = (settings) => {
    const sanitized = { ...settings };
    const intFields = [
      'BP_H_Medication', 'BP_H_Allergies', 'BP_H_Ailments', 'BP_H_Disabilities',
      'BP_H_Lifting', 'BP_H_Abstain_Alc', 'BP_H_Abstain_Tob', 'BP_H_Tetanus'
    ];
    const booleanFields = [
      'BP_H_EPIPen', 'BP_H_BreathingIssues', 'BP_H_Seizures', 'BP_H_Diabetes',
      'BP_H_Diabetes_OralMed', 'BP_H_BloodPressure', 'BP_H_BloodPressure_Med',
      'BP_H_HeartDisease'
    ];

    intFields.forEach(field => {
      if (field in sanitized) {
        if (sanitized[field] === true || sanitized[field] === 1) sanitized[field] = 1;
        else if (sanitized[field] === false || sanitized[field] === 0) sanitized[field] = 0;
      }
    });

    booleanFields.forEach(field => {
      if (field in sanitized) {
        if (sanitized[field] === 1 || sanitized[field] === true) sanitized[field] = true;
        else if (sanitized[field] === 0 || sanitized[field] === false) sanitized[field] = false;
      }
    });

    return sanitized;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!User || !User.UserID) {
      enqueueSnackbar("Please wait for data to load before saving", { variant: 'warning' });
      return;
    }

    const sanitizedUserSettings = sanitizeUserSettings(UserSettings);

    setSaving(true);
    try {
      await axios.put(endpoint, { user: User, userSettings: sanitizedUserSettings });
      enqueueSnackbar("Health Section Saved!");
    } catch (ex) {
      console.error('Error submitting health data:', ex);
      const errorMsg = ex.response?.data?.error || ex.response?.data?.details || ex.message;
      enqueueSnackbar("Health Section Save Failed: " + errorMsg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div id="tab6">
        <div className="row px-5 pt-5">
          <div className="col-md-6">
            {/* Medication */}
            <div className="health-selection-wrapper">
              <div className="checkboxes-and-radios">
                <input
                  type="checkbox"
                  id="BP_H_Medication"
                  checked={isChecked(UserSettings?.BP_H_Medication)}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="BP_H_Medication">Do you take any medication(s)?</label>
              </div>
              {UserSettings?.BP_H_Medication ?
                <div className="form-group mt-2">
                  <textarea
                    id="BP_H_Medication_Reason"
                    className="form-control"
                    rows="3"
                    value={UserSettings?.BP_H_Medication_Reason || ''}
                    placeholder="If yes, specify medications."
                    onChange={handleFieldChange}
                  ></textarea>
                </div>
                : ""}
            </div>

            {/* Allergies */}
            <div className="health-selection-wrapper">
              <div className="checkboxes-and-radios">
                <input
                  type="checkbox"
                  id="BP_H_Allergies"
                  checked={isChecked(UserSettings?.BP_H_Allergies)}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="BP_H_Allergies">Do you have any allergies or dietary restrictions?</label>
              </div>
              {UserSettings?.BP_H_Allergies ?
                <div className="form-group mt-2">
                  <textarea
                    className="form-control"
                    rows="3"
                    id="BP_H_Allergies_Reason"
                    value={UserSettings?.BP_H_Allergies_Reason || ''}
                    placeholder="If yes, specify allergies or dietary restrictions."
                    onChange={handleFieldChange}
                  ></textarea>
                </div>
                : ""}
            </div>

            {/* Lifting Restrictions */}
            <div className="health-selection-wrapper">
              <div className="checkboxes-and-radios">
                <input
                  type="checkbox"
                  id="BP_H_Lifting"
                  checked={isChecked(UserSettings?.BP_H_Lifting)}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="BP_H_Lifting">Do you have any lifting restrictions?</label>
              </div>
              {UserSettings?.BP_H_Lifting ?
                <div className="form-group mt-2">
                  <textarea
                    className="form-control"
                    rows="3"
                    id="BP_H_Lifting_Reason"
                    value={UserSettings?.BP_H_Lifting_Reason || ''}
                    placeholder="If yes, specify lifting restrictions."
                    onChange={handleFieldChange}
                  ></textarea>
                </div>
                : ""}
            </div>

            {/* Chronic Ailments */}
            <div className="health-selection-wrapper">
              <div className="checkboxes-and-radios">
                <input
                  type="checkbox"
                  id="BP_H_Ailments"
                  checked={isChecked(UserSettings?.BP_H_Ailments)}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="BP_H_Ailments">Do you have any chronic ailments?</label>
              </div>
              {UserSettings?.BP_H_Ailments ?
                <div className="form-group mt-2">
                  <textarea
                    className="form-control"
                    rows="3"
                    id="BP_H_Ailments_Reason"
                    value={UserSettings?.BP_H_Ailments_Reason || ''}
                    placeholder="If yes, specify chronic ailments."
                    onChange={handleFieldChange}
                  ></textarea>
                </div>
                : ""}
            </div>

            {/* Tetanus Shot */}
            <div className="health-selection-wrapper">
              <div className="checkboxes-and-radios">
                <input
                  type="checkbox"
                  id="BP_H_Tetanus"
                  checked={isChecked(UserSettings?.BP_H_Tetanus)}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="BP_H_Tetanus">Have you had a tetanus shot?</label>
              </div>
              {UserSettings?.BP_H_Tetanus ?
                <div className="form-group mt-2">
                  <div className="form-group">
                    <label htmlFor="BP_H_Tetanus_Year">If yes, what was the year of last tetanus shot?</label>
                    <select
                      className="form-control"
                      id="BP_H_Tetanus_Year"
                      value={UserSettings?.BP_H_Tetanus_Year || ''}
                      onChange={handleFieldChange}
                    >
                      <option value="">Select year</option>
                      {yearsList.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
                : ""}
            </div>

            {/* Abstain from Alcohol */}
            <div className="health-selection-wrapper">
              <div className="checkboxes-and-radios">
                <input
                  type="checkbox"
                  id="BP_H_Abstain_Alc"
                  checked={isChecked(UserSettings?.BP_H_Abstain_Alc)}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="BP_H_Abstain_Alc">Do you agree to abstain from the use of alcohol/illegal drugs while participating in missions/projects?</label>
              </div>
              {UserSettings?.BP_H_Abstain_Alc ?
                <div className="form-group mt-2">
                  <textarea
                    className="form-control"
                    rows="3"
                    id="BP_H_Abstain_Alc_Reason"
                    value={UserSettings?.BP_H_Abstain_Alc_Reason || ''}
                    placeholder="If no, provide reason."
                    onChange={handleFieldChange}
                  ></textarea>
                </div>
                : ""}
            </div>

            {/* Abstain from Tobacco */}
            <div className="health-selection-wrapper">
              <div className="checkboxes-and-radios">
                <input
                  type="checkbox"
                  id="BP_H_Abstain_Tob"
                  checked={isChecked(UserSettings?.BP_H_Abstain_Tob)}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="BP_H_Abstain_Tob">Do you agree to abstain from the use of tobacco while participating in missions/projects?</label>
              </div>
              {UserSettings?.BP_H_Abstain_Tob ?
                <div className="form-group mt-2">
                  <textarea
                    className="form-control"
                    rows="3"
                    id="BP_H_Abstain_Tob_Reason"
                    value={UserSettings?.BP_H_Abstain_Tob_Reason || ''}
                    placeholder="If no, provide reason."
                    onChange={handleFieldChange}
                  ></textarea>
                </div>
                : ""}
            </div>

            {/* High Blood Pressure */}
            <div className="health-selection-wrapper">
              <div className="checkboxes-and-radios">
                <input
                  type="checkbox"
                  id="BP_H_BloodPressure"
                  checked={isChecked(UserSettings?.BP_H_BloodPressure)}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="BP_H_BloodPressure">Do you have High Blood Pressure?</label>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            {/* Blood Pressure Medication */}
            <div className="health-selection-wrapper">
              <div className="checkboxes-and-radios">
                <input
                  type="checkbox"
                  id="BP_H_BloodPressure_Med"
                  checked={isChecked(UserSettings?.BP_H_BloodPressure_Med)}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="BP_H_BloodPressure_Med">Do you take medication to control your Blood Pressure?</label>
              </div>
              {UserSettings?.BP_H_BloodPressure_Med ?
                <div className="form-group mt-2">
                  <textarea
                    className="form-control"
                    rows="3"
                    id="BP_H_BloodPressure_Med_Other"
                    value={UserSettings?.BP_H_BloodPressure_Med_Other || ''}
                    placeholder="If yes, list medications."
                    onChange={handleFieldChange}
                  ></textarea>
                </div>
                : ""}
            </div>

            {/* Heart Disease */}
            <div className="health-selection-wrapper">
              <div className="checkboxes-and-radios">
                <input
                  type="checkbox"
                  id="BP_H_HeartDisease"
                  checked={isChecked(UserSettings?.BP_H_HeartDisease)}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="BP_H_HeartDisease">Do you have a history of heart disease?</label>
              </div>
              {UserSettings?.BP_H_HeartDisease ?
                <div className="form-group">
                  <label htmlFor="BP_H_HeartDisease_Events">Have you ever had any of the following?</label>
                  <select
                    className="form-control"
                    id="BP_H_HeartDisease_Events"
                    value={UserSettings?.BP_H_HeartDisease_Events || ''}
                    onChange={handleFieldChange}
                  >
                    <option value="">Select option</option>
                    <option>Heart Attack</option>
                    <option>Stent Placement</option>
                    <option>Open Heart Surgery/Bypass</option>
                    <option>Valve Surgery</option>
                  </select>
                </div>
                : ""}
            </div>

            {/* Diabetes */}
            <div className="health-selection-wrapper">
              <div className="checkboxes-and-radios">
                <input
                  type="checkbox"
                  id="BP_H_Diabetes"
                  checked={isChecked(UserSettings?.BP_H_Diabetes)}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="BP_H_Diabetes">Do you have Diabetes?</label>
              </div>
            </div>

            {/* Diabetes Medication */}
            <div className="health-selection-wrapper">
              <div className="checkboxes-and-radios">
                <input
                  type="checkbox"
                  id="BP_H_Diabetes_OralMed"
                  checked={isChecked(UserSettings?.BP_H_Diabetes_OralMed)}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="BP_H_Diabetes_OralMed">Do you take Oral Medication or Insulin for Diabetes?</label>
              </div>
            </div>

            {/* EPI Pen */}
            <div className="health-selection-wrapper">
              <div className="checkboxes-and-radios">
                <input
                  type="checkbox"
                  id="BP_H_EPIPen"
                  checked={isChecked(UserSettings?.BP_H_EPIPen)}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="BP_H_EPIPen">Do you have an EPI Pen?</label>
              </div>
            </div>

            {/* Seizures */}
            <div className="health-selection-wrapper">
              <div className="checkboxes-and-radios">
                <input
                  type="checkbox"
                  id="BP_H_Seizures"
                  checked={isChecked(UserSettings?.BP_H_Seizures)}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="BP_H_Seizures">Do you have a history of Seizures?</label>
              </div>
            </div>

            {/* Breathing Devices */}
            <div className="health-selection-wrapper">
              <div className="checkboxes-and-radios">
                <input
                  type="checkbox"
                  id="BP_H_BreathingIssues"
                  checked={isChecked(UserSettings?.BP_H_BreathingIssues)}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="BP_H_BreathingIssues">Do you use a breathing devices?</label>
              </div>
              {UserSettings?.BP_H_BreathingIssues ?
                <div className="form-group">
                  <label htmlFor="BP_H_BreathingIssues_Items">Please select</label>
                  <select
                    className="form-control"
                    id="BP_H_BreathingIssues_Items"
                    value={UserSettings?.BP_H_BreathingIssues_Items || ''}
                    onChange={handleFieldChange}
                  >
                    <option value="">Select option</option>
                    <option>Home Oxygen</option>
                    <option>CPAP</option>
                    <option>BiPAP</option>
                    <option>Inhaler</option>
                    <option>Nebulizer</option>
                  </select>
                </div>
                : ""}
            </div>

            {/* Disabilities */}
            <div className="health-selection-wrapper">
              <div className="checkboxes-and-radios">
                <input
                  type="checkbox"
                  id="BP_H_Disabilities"
                  checked={isChecked(UserSettings?.BP_H_Disabilities)}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="BP_H_Disabilities">Do you have any disabilities?</label>
              </div>
              {UserSettings?.BP_H_Disabilities ?
                <div className="form-group mt-2">
                  <textarea
                    className="form-control"
                    id="BP_H_Disabilities_Reason"
                    value={UserSettings?.BP_H_Disabilities_Reason || ''}
                    rows="3"
                    placeholder="If yes, please specify disabilities."
                    onChange={handleFieldChange}
                  ></textarea>
                </div>
                : ""}
            </div>
          </div>
        </div>
        <hr />
        {
          !props.UseFormContext ?
        <div className="row my-5">
          <div className="col-md-12 text-center">
            <a className="button-default" type='submit' onClick={handleSubmit} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minHeight: '38px', minWidth: '120px', padding: '8px 20px', opacity: saving ? 0.7 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
              {saving && <CircularProgress size={14} style={{ color: 'white' }} />}
              Save
            </a>
          </div>
        </div> : "" }
      </div>
    </form>
  )
}

export default Health;
